// components/portal/BrandLocationsMap.tsx
'use client';

import React, { useEffect, useRef, memo, useMemo, useCallback } from 'react';
import { LeasingBrandLocationDTO, LeasingBrandScanHistoryEntry } from '#types/types';
import { ChevronDown, ExternalLink, Landmark, Maximize2, Minimize2, ScanLine } from '@/components/ui/lucide-memo';
import * as d3 from 'd3';
import * as topojson from 'topojson-client';
// mapbox-gl positions its own canvas from this stylesheet; without it the tiles do not size to
// the container. Imported here rather than in globals.css so it travels with the only component
// that uses it.
import 'mapbox-gl/dist/mapbox-gl.css';
import { useKernel } from '@/kernel/Kernel';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useKernelStore } from '@/kernel/kernelStores';
import { EARTH_RADIUS_KM, haversineDistance } from '#lib/geo-distance';
import { isoNumericToCountryCode } from '#lib/country-codes';
import { resolveActiveStoreLocations } from '#lib/brands/activeStoreLocations';

// --- Types ---

interface BrandLocationsMapProps {
  brandIds?: string[];
  propertyIds?: string[];
  locations?: LeasingBrandLocationDTO[]; 
  onCountrySelect?: (countryCode: string) => void;
  onLocationSelect?: (location: LeasingBrandLocationDTO) => void;
  isSelectionMode: boolean;
  focusedLocation?: LeasingBrandLocationDTO | null;
  /** The focus came from a city pick: zoom into the city, even when its store is already on screen. */
  zoomToFocus?: boolean;
  brandName?: string;
  scanHistory?: LeasingBrandScanHistoryEntry[];
  centerOverride?: { lat: number; lng: number };
  /**
   * How wide, in km, to frame around the target when the camera has nothing else to frame (a
   * shortlist with no store points yet). Not drawn: there is no catchment ring on the map.
   */
  targetRadiusKm?: number;
  /**
   * Draw a marker over each targeted asset, on top of the stores. Off means the layer is not
   * drawn at all, not merely hidden: the point of the toggle is to read the store cloud on its own.
   */
  showAssets?: boolean;
  /** Bottom-right map controls (full view, asset layer). Off for embedded/preview surfaces. */
  showMapControls?: boolean;
  /** Makes the store count a toggle (the panel's top cities list), collapsed until pressed. */
  onCountToggle?: () => void;
  countOpen?: boolean;
  onToggleAssets?: () => void;
  /**
   * Promote the map to the main content area, or send it back. Not wired means Full view only
   * reframes the camera, which is all it can honestly do inside a fixed box.
   */
  onToggleExpand?: () => void;
  isExpanded?: boolean;
  /** Clicking an asset marker. Not wired means the markers stay decorative and unhoverable. */
  onAssetSelect?: (propertyId: string) => void;
  /** The asset whose card is open; its marker renders lit, like a focused store point. */
  selectedAssetId?: string | null;
}

interface ScanState {
    lat: number;
    lon: number;
    radiusKm: number;
    active: boolean;
    opacity: number; 
}

interface ProjectedPoint {
    x: number;
    y: number;
    original: LeasingBrandLocationDTO;
}

// --- Constants ---

// Served from our own /public, NOT a runtime CDN fetch. The jsdelivr fetch was unreliable in the
// deployed env and left the map with no country outlines ("carte noire, on ne sait pas qu'on est en
// France", 2026-07-22 Altarea call). A same-origin asset always loads; the CDN is only a fallback.
const WORLD_ATLAS_URL = '/geo/countries-50m.json';
const WORLD_ATLAS_FALLBACK_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json';
// Country geography must READ as geography on the dark app background: a visible slate fill with a
// lighter border, not the near-black #1a1a1a/#333 that disappeared into the page.
// --- Basemap (roads, regions, cities) ---
//
// The map used to draw ONE file, countries-50m.json: country outlines and nothing else. At the
// zoom levels this view actually gets used at there was nothing to orient by, no roads, no
// regions, no city names, so a cluster of pins told you a brand had stores "somewhere near
// Paris" and no more.
//
// Real vector tiles fix that, and mapbox-gl was ALREADY a dependency (3.24.0) with a valid public
// pk. token already in the env, unused by any component, so this adds no dependency and no new
// credential. Natural Earth region/road files were the alternative and were rejected: 25-40MB
// each, no street-level detail, and they would need a simplification step the repo does not have.
//
// The d3 layers are NOT replaced. d3 still owns interaction, projection, the scan rings and the
// store points; Mapbox is a non-interactive image underneath whose camera is driven from d3's
// transform every frame (see syncBasemapCamera). That keeps the store points, the whole point of
// the map, on exactly the code that already worked, and confines the change to what is behind
// them. A GL rewrite would have put a 550-line interaction model at risk for a backdrop.
const MAPBOX_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN ?? '';
// Dark basemap so the pins stay the brightest thing on screen. Mapbox's own dark style already
// carries roads, admin boundaries and place labels with sensible per-zoom LOD.
const BASEMAP_STYLE = 'mapbox://styles/mapbox/dark-v11';
// Repainted in the app's black (owner, 2026-10-03, /chatgptize): land dark grey, sea black, roads
// and borders faint, names grey, so the stores stay the brightest thing on the map.
const recolourBasemap = (map: import('mapbox-gl').Map) => {
  for (const layer of map.getStyle()?.layers ?? []) {
    try {
      if (layer.type === 'background') map.setPaintProperty(layer.id, 'background-color', '#171717');
      else if (layer.type === 'fill') map.setPaintProperty(layer.id, 'fill-color', /water/.test(layer.id) ? '#000000' : '#1C1C1C');
      else if (layer.type === 'line') map.setPaintProperty(layer.id, 'line-color', /water/.test(layer.id) ? '#000000' : /admin|boundary/.test(layer.id) ? '#3A3A3A' : '#262626');
      else if (layer.type === 'symbol') {
        map.setPaintProperty(layer.id, 'text-color', '#8E8E8E');
        map.setPaintProperty(layer.id, 'text-halo-color', '#000000');
      }
    } catch { /* a layer without that paint property keeps its own */ }
  }
  map.setFog({ color: '#000000', 'high-color': '#000000', 'space-color': '#000000', 'star-intensity': 0, 'horizon-blend': 0.02 });
};
// d3.geoMercator().scale(s) spans the world in 2*PI*s projection units; Mapbox spans it in
// 512*2^zoom screen px. Both are Web Mercator, so equating the two gives an exact zoom.
const D3_MERCATOR_SCALE = 150;
const D3_WORLD_WIDTH = 2 * Math.PI * D3_MERCATOR_SCALE;
const MAPBOX_TILE_SIZE = 512;
// How far in the user can go. The old ceiling was a d3 scale of 150, which is Mapbox zoom ~8 ,
// regional, so a store cluster never resolved into actual streets. Expressed as the Mapbox zoom
// we want and converted back, because the meaningful number is "z20 = building level", not "150x".
const MAX_BASEMAP_ZOOM = 20;
const MAX_D3_SCALE = (MAPBOX_TILE_SIZE * Math.pow(2, MAX_BASEMAP_ZOOM)) / D3_WORLD_WIDTH;
// A city fills the view around Mapbox zoom 11.
const CITY_D3_SCALE = (MAPBOX_TILE_SIZE * Math.pow(2, 11)) / D3_WORLD_WIDTH;

const COUNTRY_FILL = '#262A33';
const COUNTRY_FILL_HOVER = '#333945';
const COUNTRY_STROKE = '#4B5563';
// With a real basemap underneath, an opaque country fill would hide it. The paths stay in the DOM
// (they carry the country hover/click hit-testing that drives onCountrySelect) but become
// invisible; only the borders are kept, brighter, to read against the tiles.
const BASEMAP_ACTIVE = MAPBOX_TOKEN.length > 0;
const COUNTRY_FILL_EFFECTIVE = BASEMAP_ACTIVE ? 'transparent' : COUNTRY_FILL;
const COUNTRY_FILL_HOVER_EFFECTIVE = BASEMAP_ACTIVE ? 'rgba(255,255,255,0.08)' : COUNTRY_FILL_HOVER;
// The basemap already draws every administrative boundary. Keeping the d3 outlines on top of it
// drew each border TWICE, slightly offset at the seams, that is half of "two maps overlapping".
// The paths stay in the DOM because they carry the country hover/click hit-testing behind
// onCountrySelect; they are simply not painted.
const COUNTRY_STROKE_EFFECTIVE = BASEMAP_ACTIVE ? 'transparent' : COUNTRY_STROKE;
const CLICK_TOLERANCE = 10;
// Static decoration styles. Module scope, not inline literals: they never depend on state, and a
// fresh object each render is a new prop on a node that sits under continuous d3 interaction.
const EMPTY_GRID_STYLE: React.CSSProperties = {
  backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)',
  backgroundSize: '30px 30px',
};
const TOOLTIP_ANCHOR_STYLE: React.CSSProperties = { transform: 'translate(-50%, -100%)' };
const LERP_FACTOR = 0.15;
const DRIFT_FACTOR = 0.015; 
const SNAP_THRESHOLD_KM = 500; 
const INACTIVE_SCAN_STATE: ScanState = { lat: 0, lon: 0, radiusKm: 0, active: false, opacity: 0 };

// THE ICON FOR AN ASSET IS THE ONE THE REST OF THE APP USES: Landmark, per entityIcon('Property')
// in components/portal/entityGraph.ts. Copied here as raw path data rather than imported, because
// this layer is painted by d3 and not by React, so there is no component to mount. Lucide's 24x24
// grid, stroked and unfilled, which is why the marker is drawn twice below.
const ASSET_ICON_PATHS = [
  'M10 18v-7',
  'M11.12 2.198a2 2 0 0 1 1.76.006l7.866 3.847c.476.233.31.949-.22.949H3.474c-.53 0-.695-.716-.22-.949z',
  'M14 18v-7',
  'M18 18v-7',
  'M3 22h18',
  'M6 18v-7',
];
// Halo first, then the glyph. A thin grey stroke alone disappears wherever the marker lands on a
// light road or a label, which on a city centre is most of the time. `role` classes the two passes
// so hover can recolour the glyph without touching the halo that makes it readable.
const ASSET_GLYPH_CLASS = 'asset-glyph';
const ASSET_ICON_PASSES = [
  { role: 'asset-halo', stroke: '#0A0A0A', width: 4, opacity: 0.65 },
  { role: ASSET_GLYPH_CLASS, stroke: '#B5B5B5', width: 2, opacity: 1 },
];

const translations = {
  en: {
    locsLabel: 'stores',
    selectTargetCountry: 'Select a target country',
    timeAgo: (value: string) => `${value} ago`,
    foundLabel: (count: number) => `${count} found`,
    neverScanned: 'Never scanned',
    locationLabel: 'Location',
    closed: 'CLOSED',
    searchMaps: 'Search Area in Google Maps',
    fullView: 'Full view',
    exitFullView: 'Exit full view',
    assetsLayer: 'Assets',
    assetsShow: 'Show the assets this shortlist is sourcing for',
    assetsHide: 'Hide the targeted assets',
  },
  fr: {
    locsLabel: 'magasins',
    selectTargetCountry: 'Sélectionnez un pays cible',
    timeAgo: (value: string) => `il y a ${value}`,
    foundLabel: (count: number) => `${count} trouves`,
    neverScanned: 'Jamais scanne',
    locationLabel: 'Localisation',
    closed: 'FERME',
    searchMaps: 'Rechercher la zone sur Google Maps',
    fullView: 'Vue complete',
    exitFullView: 'Quitter la vue complete',
    assetsLayer: 'Actifs',
    assetsShow: 'Afficher les actifs vises par cette liste',
    assetsHide: 'Masquer les actifs cibles',
  },
};

const BrandLocationsMap = memo(({ 
  brandIds = [], 
  propertyIds = [], 
  locations: manualLocations = [], 
  onCountrySelect, 
  onLocationSelect, 
  isSelectionMode, 
  focusedLocation: requestedFocus,
  zoomToFocus = false,
  brandName,
  scanHistory,
  centerOverride ,
  targetRadiusKm,
  showAssets = true,
  showMapControls = false,
  onCountToggle,
  countOpen = false,
  onToggleAssets,
  onToggleExpand,
  isExpanded = false,
  onAssetSelect,
  selectedAssetId,
}: BrandLocationsMapProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore(state => state.language);
  const t = translations[lang];
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mapGroupRef = useRef<SVGGElement>(null);
  const scanLayerRef = useRef<SVGGElement>(null);
  const targetLayerRef = useRef<SVGGElement>(null);
  const scanPathRef = useRef<SVGPathElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const hoveredPointIdRef = useRef<string | null>(null);

  const topologyRef = useRef<any>(null);
  const basemapHostRef = useRef<HTMLDivElement>(null);
  const basemapRef = useRef<import('mapbox-gl').Map | null>(null);
  const zoomBehavior = useRef<d3.ZoomBehavior<Element, unknown> | null>(null);
  
  const projection = useRef(d3.geoMercator().scale(150).translate([0, 0]));
  const pathGenerator = useRef(d3.geoPath().projection(projection.current));
  const currentTransform = useRef(d3.zoomIdentity);

  // --- DATA RESOLUTION FROM STORE ---
  const brandRegistry = useKernelStore(state => state.inventory.Brand.registry);
  const propertyRegistry = useKernelStore(state => state.inventory.Property.registry);

  const resolvedLocations = useMemo(() => {
    const lists = brandIds.map(id => brandRegistry[id]?.locations);
    const inventory = lists.flatMap(list => list ?? []);
    if (lists.length > 0 && lists.every(Array.isArray)) return resolveActiveStoreLocations(manualLocations, inventory);
    // A missing brand must not discard current closure data from brands already loaded.
    const loadedIds = brandIds.filter((_, index) => Array.isArray(lists[index]));
    return resolveActiveStoreLocations(manualLocations, inventory, loadedIds);
  }, [brandIds, brandRegistry, manualLocations]);

  const resolvedProperties = useMemo(() => {
    return propertyIds.map(id => propertyRegistry[id]).filter(Boolean);
  }, [propertyIds, propertyRegistry]);

  // --- ANIMATION & DRIFT REFS ---
  const targetScanRef = useRef<ScanState>({ ...INACTIVE_SCAN_STATE });
  const currentScanRef = useRef<ScanState>({ ...INACTIVE_SCAN_STATE });
  const isHoveredRef = useRef(false);
  const driftTargetTransformRef = useRef<d3.ZoomTransform | null>(null);
  /** The set of store points the camera was last fitted to, so a re-render does not re-fit. */
  const framedStoresRef = useRef<string | null>(null);
  const hadMappedStoresRef = useRef(false);
  const animationFrameRef = useRef<number | null>(null);

  const projectedPoints = useMemo(() => {
    const points: ProjectedPoint[] = [];
    for (const loc of resolvedLocations) {
        const proj = projection.current([loc.longitude!, loc.latitude!]);
        if (proj && proj.every(Number.isFinite)) {
            points.push({ x: proj[0], y: proj[1], original: loc });
        }
    }
    return points;
  }, [resolvedLocations]);

  const focusedLocation = requestedFocus?.id
    ? projectedPoints.find(point => point.original.id === requestedFocus.id)?.original ?? null : null;

  const propsRef = useRef({ resolvedLocations, onCountrySelect, onLocationSelect, isSelectionMode, brandName, focusedLocation, projectedPoints, scanHistory, t, lang, centerOverride, resolvedProperties, targetRadiusKm, showAssets, onAssetSelect, selectedAssetId });

  useEffect(() => {
    propsRef.current = { resolvedLocations, onCountrySelect, onLocationSelect, isSelectionMode, brandName, focusedLocation, projectedPoints, scanHistory, t, lang, centerOverride, resolvedProperties, targetRadiusKm, showAssets, onAssetSelect, selectedAssetId };
  }, [resolvedLocations, onCountrySelect, onLocationSelect, isSelectionMode, brandName, focusedLocation, projectedPoints, scanHistory, centerOverride, resolvedProperties, targetRadiusKm, t, lang, showAssets, onAssetSelect, selectedAssetId]);

  /**
   * Drive the Mapbox camera from d3's transform so the tiles sit exactly under the pins.
   *
   * Both are Web Mercator, so the two are related by closed form rather than by tracking:
   *   the world spans 2*PI*scale projection units in d3 and 512*2^zoom px in Mapbox, and the d3
   *   transform scales projection units to px by t.k, so 512*2^zoom = t.k * 2*PI*scale.
   * The centre is the projection point currently under the middle of the viewport, un-projected.
   * jumpTo, never easeTo: this runs on d3's zoom events, and an animated camera would lag the
   * pins by the animation duration, which reads as the basemap sliding under them.
   */
  const syncBasemapCamera = useCallback(() => {
    const map = basemapRef.current;
    const container = containerRef.current;
    if (!map || !container) return;
    const width = container.clientWidth;
    const height = container.clientHeight;
    if (width <= 0 || height <= 0) return;
    const t = currentTransform.current;
    if (!Number.isFinite(t.k) || t.k <= 0) return;

    const zoom = Math.log2((t.k * D3_WORLD_WIDTH) / MAPBOX_TILE_SIZE);
    const centreProjected: [number, number] = [(width / 2 - t.x) / t.k, (height / 2 - t.y) / t.k];
    const centre = projection.current.invert?.(centreProjected);
    if (!centre || !Number.isFinite(centre[0]) || !Number.isFinite(centre[1])) return;
    // Mercator has no data beyond ~±85.05 and Mapbox rejects a centre outside it.
    const lat = Math.max(-85, Math.min(85, centre[1]));
    const lng = ((((centre[0] + 180) % 360) + 360) % 360) - 180;
    try {
      map.jumpTo({ center: [lng, lat], zoom: Math.max(0, Math.min(22, zoom)) });
    } catch {
      /* a camera the style has not finished loading yet; the next sync catches up */
    }
  }, []);

  /**
   * Where a store sits on screen.
   *
   * With a globe basemap the world is no longer flat, so d3's Mercator coordinates put the pins
   * in the wrong place, the further from the centre, the more wrong. Asking the map to project
   * the real lat/lng is correct under ANY projection, and it keeps following Mapbox as globe
   * eases into Mercator on the way in. Falls back to the d3 transform when there is no basemap,
   * which is also the path used before this existed.
   */
  const screenXY = useCallback((p: ProjectedPoint): [number, number] => {
    const map = basemapRef.current;
    const lng = p.original.longitude;
    const lat = p.original.latitude;
    if (map && typeof lng === 'number' && typeof lat === 'number') {
      try {
        const projected = map.project([lng, lat]);
        if (Number.isFinite(projected.x) && Number.isFinite(projected.y)) return [projected.x, projected.y];
      } catch {
        /* map not ready yet, fall through to the d3 transform */
      }
    }
    const t = currentTransform.current;
    return [t.applyX(p.x), t.applyY(p.y)];
  }, []);

  // Declared here, above its callers, because it is now a stable callback they list as a
  // dependency. It reads nothing but refs, so the empty dependency list is the honest one.
  const drawCanvasDots = useCallback((points: ProjectedPoint[]) => {
      const canvas = canvasRef.current; if (!canvas) return;
      const ctx = canvas.getContext('2d'); if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const baseRadius = Math.min(window.innerWidth, window.innerHeight) * 0.002;
      const focusedId = propsRef.current.focusedLocation?.id;
      const hoveredId = hoveredPointIdRef.current;
      points.forEach(p => {
          const [x, y] = screenXY(p);
          if (!Number.isFinite(x) || !Number.isFinite(y) || x < -10 || x > canvas.width + 10 || y < -10 || y > canvas.height + 10) return;
          const isFocused = p.original.id === focusedId;
          const isHovered = p.original.id === hoveredId;
          // Green = freshly added, not yet passed by the store reviewer (SRC-035): the dot turns
          // white by itself once the reviewer stamps the row.
          // Greys only (/chatgptize): white open, light grey not yet reviewed, a hollow grey ring
          // closed, and the selected store white with a ring around it.
          const color = isFocused ? '#FFFFFF' : p.original.isClosed ? '#8E8E8E' : p.original.pendingReview ? '#AFAFAF' : '#ffffff';
          const hollow = p.original.isClosed && !isFocused;
          // Hover: a soft halo + a larger dot so the point the cursor is over is obvious.
          if (isHovered && !isFocused) {
              ctx.beginPath(); ctx.globalAlpha = 0.25; ctx.fillStyle = color;
              ctx.arc(x, y, baseRadius * 3.2, 0, 2 * Math.PI); ctx.fill();
          }
          const r = isFocused ? baseRadius * 1.5 : isHovered ? baseRadius * 2 : baseRadius;
          // Over a real basemap a 2px white dot competes with road casings and place labels. A
          // dark disc and rim behind it buy contrast against ANY tile content without changing
          // the dot's own colour language, so the stores stay the brightest thing on the map ,
          // which is the whole point of this view. Skipped on the plain dark background, where
          // there is nothing to separate from.
          if (BASEMAP_ACTIVE) {
              ctx.beginPath(); ctx.globalAlpha = 0.55; ctx.fillStyle = '#000000';
              ctx.arc(x, y, r * 2.1, 0, 2 * Math.PI); ctx.fill();
          }
          ctx.beginPath();
          ctx.globalAlpha = isFocused ? 1.0 : isHovered ? 1.0 : 0.9;
          ctx.arc(x, y, r, 0, 2 * Math.PI);
          if (hollow) {
              ctx.lineWidth = Math.max(1, r * 0.5); ctx.strokeStyle = color; ctx.stroke();
              return;
          }
          ctx.fillStyle = color; ctx.fill();
          if (isFocused) {
              ctx.beginPath(); ctx.globalAlpha = 0.9; ctx.lineWidth = 1.5; ctx.strokeStyle = '#FFFFFF';
              ctx.arc(x, y, r * 2.6, 0, 2 * Math.PI); ctx.stroke();
              ctx.beginPath(); ctx.arc(x, y, r, 0, 2 * Math.PI);
          }
          if (BASEMAP_ACTIVE) {
              ctx.globalAlpha = 0.9; ctx.lineWidth = Math.max(0.75, r * 0.35);
              ctx.strokeStyle = 'rgba(0,0,0,0.85)'; ctx.stroke();
          }
      });
  }, [screenXY]);

  const updateViewAndDots = useCallback(() => {
    if (!svgRef.current || !containerRef.current) return;
    syncBasemapCamera();
    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;
    const t = currentTransform.current;

    const vW = width / t.k;
    const vH = height / t.k;
    const vX = -t.x / t.k;
    const vY = -t.y / t.k;
    d3.select(svgRef.current).attr('viewBox', `${vX} ${vY} ${vW} ${vH}`);

    // Update Target Pin Scale (Inverse of zoom to keep fixed size)
    if (targetLayerRef.current) {
        const pinScale = 0.8 / t.k; 
        d3.select(targetLayerRef.current).selectAll('g.target-pin')
          .attr('transform', (d: any) => `translate(${d.x}, ${d.y}) scale(${pinScale})`);
    }

    drawCanvasDots(propsRef.current.projectedPoints);
  }, [syncBasemapCamera, drawCanvasDots]);

  const animate = useCallback(() => {
    if (scanPathRef.current) {
        const target = targetScanRef.current;
        const current = currentScanRef.current;
        current.opacity += ((target.active ? 1 : 0) - current.opacity) * 0.1;
        if (current.opacity > 0.01) {
            const dist = haversineDistance(current.lat, current.lon, target.lat, target.lon);
            if (dist > SNAP_THRESHOLD_KM) {
                current.lat = target.lat; current.lon = target.lon; current.radiusKm = target.radiusKm;
            } else {
                current.lat += (target.lat - current.lat) * LERP_FACTOR;
                current.lon += (target.lon - current.lon) * LERP_FACTOR;
                current.radiusKm += (target.radiusKm - current.radiusKm) * LERP_FACTOR;
            }
            const circleGenerator = d3.geoCircle().center([current.lon, current.lat]).radius(current.radiusKm / EARTH_RADIUS_KM * (180 / Math.PI));
            d3.select(scanPathRef.current).datum(circleGenerator()).attr('d', pathGenerator.current as any).attr('opacity', current.opacity);
        } else {
            d3.select(scanPathRef.current).attr('opacity', 0);
        }
    }

    if (!isHoveredRef.current && driftTargetTransformRef.current && !targetScanRef.current.active) {
        const current = currentTransform.current;
        const target = driftTargetTransformRef.current;
        const scaleDiff = Math.abs(current.k - target.k);
        const xDiff = Math.abs(current.x - target.x);
        const yDiff = Math.abs(current.y - target.y);
        if (scaleDiff > 0.001 || xDiff > 0.1 || yDiff > 0.1) {
            const newK = current.k + (target.k - current.k) * DRIFT_FACTOR;
            const newX = current.x + (target.x - current.x) * DRIFT_FACTOR;
            const newY = current.y + (target.y - current.y) * DRIFT_FACTOR;
            const nextTransform = d3.zoomIdentity.translate(newX, newY).scale(newK);
            currentTransform.current = nextTransform;
            if (containerRef.current) (containerRef.current as any).__zoom = nextTransform;
            updateViewAndDots();
        }
    }
    animationFrameRef.current = requestAnimationFrame(animate);
  }, [updateViewAndDots]);

  useEffect(() => {
      animationFrameRef.current = requestAnimationFrame(animate);
      return () => { if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current); };
  }, [animate]);

  const updateDriftTarget = useCallback(() => {
    const { centerOverride, resolvedProperties, targetRadiusKm, resolvedLocations } = propsRef.current;

    // THE ASSET LAYER IS A LAYER, NOT A CAMERA, and the drift is a camera. The draw effect already
    // refuses to fly to the asset when there are stores to frame, but this armed the drift target
    // anyway, from every resize and every mount, so the animation loop then crept the camera onto
    // the asset a frame at a time and the store cloud slid away on its own. The rule belongs here,
    // where every caller passes, rather than at each call site: two of them had already diverged.
    if (resolvedLocations.length > 0) {
      driftTargetTransformRef.current = null;
      return;
    }

    let effectiveCenter = centerOverride;
    if (!effectiveCenter && resolvedProperties.length > 0) {
        const loc = resolvedProperties[0]?.location as any;
        if (loc && typeof loc.lat === 'number') {
            effectiveCenter = { lat: loc.lat, lng: loc.lng || loc.longitude };
        }
    }

    if (!effectiveCenter || !containerRef.current) {
        driftTargetTransformRef.current = null;
        return;
    }

    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;
    const radiusKm = targetRadiusKm || 40; 

    const circleGenerator = d3.geoCircle().center([effectiveCenter.lng, effectiveCenter.lat]).radius(radiusKm / EARTH_RADIUS_KM * (180 / Math.PI));
    const bounds = pathGenerator.current.bounds(circleGenerator());
    const [[x0, y0], [x1, y1]] = bounds;
    const dx = x1 - x0; const dy = y1 - y0; const x = (x0 + x1) / 2; const y = (y0 + y1) / 2;
    const scale = Math.max(1, Math.min(150, 0.9 / Math.max(dx / width, dy / height)));
    const translate = [width / 2 - scale * x, height / 2 - scale * y];
    driftTargetTransformRef.current = d3.zoomIdentity.translate(translate[0] ?? 0, translate[1] ?? 0).scale(scale);
  }, []);

  useEffect(() => {
    return () => {
      targetScanRef.current = { ...INACTIVE_SCAN_STATE };
      currentScanRef.current = { ...INACTIVE_SCAN_STATE };
      if (scanPathRef.current) d3.select(scanPathRef.current).attr('opacity', 0);
    };
  }, [brandName]);

  const handleResize = useCallback(() => {
    if (!containerRef.current || !canvasRef.current || !svgRef.current) return;
    canvasRef.current.width = containerRef.current.clientWidth;
    canvasRef.current.height = containerRef.current.clientHeight;
    // MAPBOX DOES NOT WATCH ITS OWN CONTAINER. Everything else here is measured off the container
    // on every resize, but the basemap kept the canvas size it was born with, so the tiles and the
    // points they sit on drifted apart the moment the box changed shape: resize the window, or
    // expand the map, and the marker was suddenly kilometres from the street it marks. Only a
    // reload put them back. resize() first, so the camera sync below runs against the new size.
    basemapRef.current?.resize();
    updateDriftTarget();
    updateViewAndDots();
  }, [updateDriftTarget, updateViewAndDots]);

  // Mount the basemap once. Non-interactive on purpose: d3 keeps every gesture, so there is no
  // second pan/zoom implementation to disagree with the first. Loaded dynamically so mapbox-gl
  // (a large, window-dependent bundle) never reaches the server render.
  useEffect(() => {
    if (!BASEMAP_ACTIVE || !basemapHostRef.current || basemapRef.current) return;
    let disposed = false;
    void (async () => {
      try {
        const mapboxgl = (await import('mapbox-gl')).default;
        if (disposed || !basemapHostRef.current) return;
        mapboxgl.accessToken = MAPBOX_TOKEN;
        const map = new mapboxgl.Map({
          container: basemapHostRef.current,
          style: BASEMAP_STYLE,
          center: [2.35, 46.6],
          zoom: 4,
          // FLAT, not the globe (owner, 2026-10-04: "the map is still broken"). With world networks
          // the globe crushed the Americas and Asia against its rim and hid half of every network
          // on its far side, so stores read as floating in the sea. A flat world shows every
          // store over its own country, and d3's zoom maps onto it exactly.
          projection: { name: 'mercator' },
          interactive: false,
          attributionControl: false,
          // The pins are drawn by d3 in a separate layer, so nothing here needs to be readable
          // through a rotated or tilted camera. Keeping it flat is also what makes the closed-form
          // camera sync exact.
          pitchWithRotate: false,
          dragRotate: false,
          renderWorldCopies: true,
          fadeDuration: 0,
        });
        basemapRef.current = map;
        map.on('load', () => {
          if (disposed) return;
          recolourBasemap(map);
          syncBasemapCamera();
        });
        map.on('error', (event: unknown) => {
          // A bad token or a blocked tile host must not take the pins down with it: the d3 layers
          // render regardless, and the country outlines are still there underneath.
          console.warn('[BrandLocationsMap] basemap error', event);
        });
      } catch (error) {
        console.warn('[BrandLocationsMap] basemap failed to load', error);
      }
    })();
    return () => {
      disposed = true;
      basemapRef.current?.remove();
      basemapRef.current = null;
    };
  }, [syncBasemapCamera]);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current || !canvasRef.current || !scanLayerRef.current) return;
    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;
    currentTransform.current = d3.zoomIdentity.translate(width / 2, height / 2);
    zoomBehavior.current = d3.zoom<Element, unknown>().scaleExtent([1, MAX_D3_SCALE]).on('zoom', (event) => {
        currentTransform.current = event.transform;
        updateViewAndDots();
    });
    d3.select(containerRef.current).call(zoomBehavior.current as any).call(zoomBehavior.current.transform as any, currentTransform.current);
    
    const scanG = d3.select(scanLayerRef.current);
    scanG.selectAll('*').remove();
    scanPathRef.current = scanG.append('path').attr('fill', 'none').attr('stroke', '#10b981').attr('stroke-width', 2).attr('vector-effect', 'non-scaling-stroke').attr('stroke-dasharray', '4 4').attr('opacity', 0).node() as SVGPathElement;
    
    const targetG = d3.select(targetLayerRef.current);
    targetG.selectAll('*').remove();

    handleResize();
    const resizeObserver = new ResizeObserver(() => handleResize());
    resizeObserver.observe(containerRef.current);
    const loadAtlas = async () => {
      for (const url of [WORLD_ATLAS_URL, WORLD_ATLAS_FALLBACK_URL]) {
        try {
          const res = await fetch(url);
          if (res.ok) return await res.json();
        } catch { /* try the next source */ }
      }
      return null;
    };
    loadAtlas().then(data => {
        if (!data?.objects?.countries) return; // never leave the map blank silently
        const countries = topojson.feature(data, data.objects.countries);
        topologyRef.current = { features: (countries as any).features, interiors: topojson.mesh(data, data.objects.countries, (a: any, b: any) => a !== b) };
        renderMapStaticLayers();
    }).catch(() => {});
    return () => resizeObserver.disconnect();
  }, [handleResize]);

  const renderMapStaticLayers = () => {
      if (!mapGroupRef.current || !topologyRef.current) return;
      const g = d3.select(mapGroupRef.current);
      g.selectAll('*').remove();
      g.selectAll('path.country').data(topologyRef.current.features).enter().append('path')
          .attr('class', 'country').attr('d', pathGenerator.current as any).attr('fill', COUNTRY_FILL_EFFECTIVE).attr('stroke', COUNTRY_STROKE_EFFECTIVE).attr('stroke-width', 0.7).attr('vector-effect', 'non-scaling-stroke')
          .on('mouseover', function(event, d: any) { 
              if (!propsRef.current.isSelectionMode) return;
              d3.select(this).attr('fill', COUNTRY_FILL_HOVER_EFFECTIVE);
              const isoCode = isoNumericToCountryCode(d.id);
              if (isoCode && tooltipRef.current) {
                  const latestScan = propsRef.current.scanHistory?.filter(h => h.countryCode === isoCode).sort((a, b) => new Date(b.lastScannedAt).getTime() - new Date(a.lastScannedAt).getTime())[0];
                  let html = `<div class="font-bold text-xs mb-1">${isoCode}</div>`;
                  if (latestScan) {
                      const timeAgo = formatDistanceToNow(new Date(latestScan.lastScannedAt), { locale: propsRef.current.lang === 'fr' ? fr : undefined });
                      html += `<div class="text-[10px] text-white/70 flex items-center gap-1"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> ${propsRef.current.t.timeAgo(timeAgo)}</div>`;
                      html += `<div class="text-[10px] ${latestScan.locationsFound > 0 ? 'text-emerald-400' : 'text-white/30'} mt-1 flex items-center gap-1"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg> ${propsRef.current.t.foundLabel(latestScan.locationsFound)}</div>`;
                  } else html += `<div class="text-[10px] text-white/30 italic">${propsRef.current.t.neverScanned}</div>`;
                  tooltipRef.current.innerHTML = html; tooltipRef.current.style.opacity = '1';
              }
          })
          .on('mousemove', function(event) {
              if (propsRef.current.isSelectionMode && tooltipRef.current) {
                  const [x, y] = d3.pointer(event, containerRef.current);
                  tooltipRef.current.style.left = `${x}px`; tooltipRef.current.style.top = `${y - 10}px`; tooltipRef.current.style.transform = `translate(-50%, -100%)`;
              }
          })
          .on('mouseout', function() { d3.select(this).attr('fill', COUNTRY_FILL_EFFECTIVE); if (tooltipRef.current) tooltipRef.current.style.opacity = '0'; })
          .on('click', (event, d: any) => {
              if (!propsRef.current.isSelectionMode) return;
              const isoCode = isoNumericToCountryCode(d.id);
              if (isoCode && propsRef.current.onCountrySelect) propsRef.current.onCountrySelect(isoCode);
          });
  };

  useEffect(() => {
      if (!zoomBehavior.current || !containerRef.current) return;
      const { centerOverride, resolvedProperties, resolvedLocations } = propsRef.current;
      
      let effectiveCenter = centerOverride;
      if (!effectiveCenter && resolvedProperties.length > 0) {
          const loc = resolvedProperties[0]?.location as any;
          if (loc && typeof loc.lat === 'number') {
              effectiveCenter = { lat: loc.lat, lng: loc.lng || loc.longitude };
          }
      }

      if (projectedPoints.length > 0) hadMappedStoresRef.current = true;
      if (projectedPoints.length === 0 && hadMappedStoresRef.current) {
          hadMappedStoresRef.current = false;
          framedStoresRef.current = null;
          d3.select(containerRef.current).interrupt();
          if (!effectiveCenter) {
              driftTargetTransformRef.current = null;
              d3.select(containerRef.current).call(zoomBehavior.current.transform as any, d3.zoomIdentity);
          }
      }
      if (targetScanRef.current.active || focusedLocation) return;

      // THE ASSET LAYER IS A LAYER, NOT A CAMERA. Passing propertyIds used to make this branch win
      // outright: the camera centred on the asset and the store cloud was left off-screen, which
      // read as "we lost the store points". Drawing and framing are now separate - assets draw
      // whenever they exist, but the camera only follows them when there are no stores to frame.
      //
      // Called UNCONDITIONALLY, because it is also what disarms the drift. The first resize fires
      // on mount, before the stores have loaded, so it armed a drift target back when there was
      // nothing to frame but the asset; nothing ever cleared it once the stores arrived, and the
      // animation loop then crept the camera onto the asset a frame at a time, forever. The map
      // never stood still. updateDriftTarget owns the rule now, so every path through here obeys
      // the same one.
      updateDriftTarget();

      if (effectiveCenter) {
          // driftTargetTransformRef is null whenever there are stores to frame, so this moves the
          // camera onto the asset only in the case where the asset is all there is.
          if (driftTargetTransformRef.current && !isHoveredRef.current) {
              d3.select(containerRef.current)
                .transition()
                .duration(2000)
                .ease(d3.easeCubicOut)
                .call(zoomBehavior.current.transform as any, driftTargetTransformRef.current);
          }

          const targetG = d3.select(targetLayerRef.current);
          targetG.selectAll('*').remove();

          // ONE PIN PER ASSET. This drew resolvedProperties[0] only, so a shortlist covering
          // three centres marked one of them and silently dropped the rest - which is precisely
          // the comparison this layer exists to support. Assets without coordinates are skipped
          // rather than stacked at 0,0 in the Gulf of Guinea.
          const assetPoints: Array<{ lat: number; lng: number; id?: string }> = [];
          if (showAssets !== false) {
            for (const property of (resolvedProperties as any[])) {
              const loc = property?.location as any;
              const lat = typeof loc?.lat === 'number' ? loc.lat : null;
              const lng =
                typeof loc?.lng === 'number' ? loc.lng
                : typeof loc?.longitude === 'number' ? loc.longitude
                : null;
              if (lat != null && lng != null) assetPoints.push({ lat, lng, id: property?.id });
            }
            // An explicit centerOverride with no asset rows still deserves its marker. It carries no
            // id, so it draws but does not open a card: there is no asset record behind it.
            if (assetPoints.length === 0) assetPoints.push({ lat: effectiveCenter.lat, lng: effectiveCenter.lng });
          }

          // A PLAIN MARKER, NO CATCHMENT RING. What used to be drawn here was a 40km dashed
          // circle, a GUESS at a zone de chalandise and a wrong one on a dense urban centre,
          // where the real catchment is a walk of a few hundred metres. The marker is drawn at a
          // fixed screen size (it divides out the zoom) so it stays legible at every scale.
          for (const asset of assetPoints) {
            const proj = projection.current([asset.lng, asset.lat]);
            if (!proj) continue;
            const pin = targetG.append('g')
              .attr('class', 'target-pin')
              .datum({ x: proj[0], y: proj[1] })
              .attr('transform', `translate(${proj[0]}, ${proj[1]}) scale(${0.8 / currentTransform.current.k})`);

            // THREE NESTED GROUPS, and each one has a single owner, which is the point:
            //   .target-pin   translate + inverse-zoom scale, rewritten by the zoom handler
            //   .target-pin-emphasis  the hover/selected swell, scaled about (0,0) = the asset
            //                         point itself, so the icon grows UP from its base and its
            //                         foot never leaves the spot it is marking
            //   the inner group  the icon's own -12/-22 offset onto that base centre
            // Flattening them would put the zoom handler and the hover state on one attribute,
            // and whichever wrote last would win.
            const emphasis = pin.append('g')
              .attr('class', 'target-pin-emphasis')
              .attr('transform', 'scale(1)')
              .style('transition', 'transform 120ms ease-out');
            const glyph = emphasis.append('g').attr('transform', 'translate(-12, -22)');
            for (const pass of ASSET_ICON_PASSES) {
              for (const d of ASSET_ICON_PATHS) {
                glyph.append('path')
                  .attr('class', pass.role)
                  .attr('d', d)
                  .attr('fill', 'none')
                  .attr('stroke', pass.stroke)
                  .attr('stroke-width', pass.width)
                  .attr('stroke-opacity', pass.opacity)
                  .attr('stroke-linecap', 'round')
                  .attr('stroke-linejoin', 'round');
              }
            }

            // A stroked icon is only hit-testable ON its strokes, which are 2px of hairline. The
            // invisible disc is the actual click target, sized to the glyph so the marker behaves
            // like a button rather than a puzzle.
            emphasis.append('circle')
              .attr('cx', 0).attr('cy', -11).attr('r', 13)
              .attr('fill', 'transparent')
              .style('cursor', asset.id ? 'pointer' : 'default');

            const setEmphasis = (on: boolean) => {
              emphasis.attr('transform', `scale(${on ? 1.25 : 1})`);
              glyph.selectAll(`path.${ASSET_GLYPH_CLASS}`).attr('stroke', on ? '#FFFFFF' : '#B5B5B5');
            };
            // Selected stays lit, so the card on screen and the marker it belongs to agree.
            if (asset.id && asset.id === selectedAssetId) setEmphasis(true);
            if (asset.id) {
              pin
                .on('mouseenter', () => setEmphasis(true))
                .on('mouseleave', () => setEmphasis(asset.id === propsRef.current.selectedAssetId))
                .on('click', (event: MouseEvent) => {
                  // The container's own onClick runs a nearest-store hit test; without this a
                  // marker sitting on top of a store would open the store's card instead.
                  event.stopPropagation();
                  propsRef.current.onAssetSelect?.(asset.id as string);
                });
            }
          }
      }
      // FRAMING IS FOR NEW DATA, NOT FOR EVERY RENDER.
      //
      // This effect draws the asset markers, so it re-runs on things that have nothing to do with
      // the camera: the selected asset, and resolvedProperties, whose identity changes on any
      // inventory tick. It used to re-frame on every one of those runs, so clicking an asset threw
      // the camera back out to the whole store cloud - the user zooms into their centre, clicks the
      // marker to read it, and lands back on a map of France. Selecting something must NEVER move
      // the camera; the store-card path below already says so, the asset path did not.
      //
      // The signature is the set of points being framed. Frame when that set is genuinely new, and
      // record it only when the framing actually ran, so a frame skipped because the cursor was
      // over the map still happens on the next pass.
      if (resolvedLocations.length > 0 && !isHoveredRef.current) {
          const valid = projectedPoints.map(point => point.original);
          const signature = valid.map(l => `${l.longitude},${l.latitude}`).join(';');
          if (valid.length > 0 && signature !== framedStoresRef.current) {
              framedStoresRef.current = signature;
              const bounds = d3.geoPath().projection(projection.current).bounds({ type: "MultiPoint", coordinates: valid.map(l => [l.longitude!, l.latitude!]) } as any);
              const [[x0, y0], [x1, y1]] = bounds;
              const scale = Math.max(1, Math.min(50, 0.85 / Math.max((x1 - x0) / containerRef.current.clientWidth, (y1 - y0) / containerRef.current.clientHeight)));
              d3.select(containerRef.current).transition().duration(1500).ease(d3.easeCubicOut).call(zoomBehavior.current.transform as any, d3.zoomIdentity.translate(containerRef.current.clientWidth / 2 - scale * (x0 + x1) / 2, containerRef.current.clientHeight / 2 - scale * (y0 + y1) / 2).scale(scale));
          }
      }
  }, [resolvedLocations, projectedPoints, focusedLocation, centerOverride, updateDriftTarget, resolvedProperties, showAssets, selectedAssetId]);

  useEffect(() => {
    if (!projectedPoints.some(point => point.original.id === hoveredPointIdRef.current)) {
      hoveredPointIdRef.current = null;
      if (tooltipRef.current) tooltipRef.current.style.opacity = '0';
      if (containerRef.current) containerRef.current.style.cursor = '';
    }
    drawCanvasDots(projectedPoints);
  }, [projectedPoints, focusedLocation]);

  // Selecting a store opens its card. It must NOT change the zoom: this used to snap the camera to
  // a hardcoded scale of 34, so clicking a point while zoomed into a street threw the user back out
  // to a regional view, they lost the place they were reading.
  //
  // The camera only moves when the point is NOT already on screen, which is the case when the
  // selection came from somewhere other than a click on the map (initialSelectedId, a list). Even
  // then it only pans, at whatever zoom the user is already on.
  useEffect(() => {
    if (!focusedLocation || focusedLocation.latitude == null || focusedLocation.longitude == null) return;
    if (!zoomBehavior.current || !containerRef.current) return;
    const proj = projection.current([focusedLocation.longitude, focusedLocation.latitude]);
    if (!proj) return;
    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;
    const t = currentTransform.current;

    // Where the point currently sits. Clicking one always lands here, so nothing moves.
    const [screenX, screenY] = screenXY({ x: proj[0], y: proj[1], original: focusedLocation });
    const margin = 24;
    const alreadyVisible =
      screenX >= margin && screenX <= width - margin && screenY >= margin && screenY <= height - margin;
    if (alreadyVisible && !zoomToFocus) return;

    driftTargetTransformRef.current = null; // stop the ambient drift so it doesn't fight the focus
    // Sit the point in the lower portion of the viewport (78% down) so it lands just below the info
    // card, which is pinned to the top edge, the point stays visible and the card never clips.
    const scale = zoomToFocus ? Math.max(t.k, CITY_D3_SCALE) : t.k; // else the user's own zoom, preserved
    const transform = d3.zoomIdentity.translate(width / 2 - scale * proj[0], height * 0.78 - scale * proj[1]).scale(scale);
    d3.select(containerRef.current).transition().duration(1100).ease(d3.easeCubicOut)
      .call(zoomBehavior.current.transform as any, transform);
  }, [focusedLocation?.id, focusedLocation?.latitude, focusedLocation?.longitude, zoomToFocus]);

  const getLocationAtEvent = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const canvas = canvasRef.current; if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left; const mouseY = e.clientY - rect.top;

    // Pick the NEAREST point within tolerance (not just the first), the France cluster is dense,
    // so "first within 10px" would grab the wrong store.
    let best: ProjectedPoint | null = null;
    let bestDist = CLICK_TOLERANCE * CLICK_TOLERANCE;
    for (const p of propsRef.current.projectedPoints) {
      const [px, py] = screenXY(p);
      const dx = px - mouseX;
      const dy = py - mouseY;
      const d2 = dx * dx + dy * dy;
      if (d2 < bestDist) { bestDist = d2; best = p; }
    }
    return best?.original || null;
  }, [screenXY]);

  const handleContainerContextMenu = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
      if (propsRef.current.isSelectionMode) return;
      const found = getLocationAtEvent(e);
      if (found && kernel.current) {
          e.preventDefault(); e.stopPropagation();
          kernel.current.setMenuData({
              position: { x: e.clientX, y: e.clientY },
              actions: [
                  { label: found.name || propsRef.current.brandName || 'Brand', onClick: () => {}, customContent: (<div className="font-bold text-xs text-white/50 pb-1 mb-1 border-b border-white/10 flex items-center justify-between gap-4"><span>{found.city || propsRef.current.t.locationLabel}</span>{found.isClosed && <span className="text-red-400 text-[9px]">{propsRef.current.t.closed}</span>}</div>) },
                  { label: propsRef.current.t.searchMaps, icon: <ExternalLink size={14} />, onClick: () => { window.open(`https://www.google.com/maps/search/${encodeURIComponent(`${propsRef.current.brandName || ''} ${found.city || ''}`)}/@${found.latitude},${found.longitude},15z`, '_blank'); } }
              ]
          });
      }
  }, [getLocationAtEvent, kernel]);

  const handleContainerMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (propsRef.current.isSelectionMode) return;
    const found = getLocationAtEvent(e);
    const nextId = found?.id ?? null;
    if (nextId !== hoveredPointIdRef.current) {
      hoveredPointIdRef.current = nextId;
      if (containerRef.current) containerRef.current.style.cursor = nextId ? 'pointer' : '';
      drawCanvasDots(propsRef.current.projectedPoints); // repaint so the hovered point grows/shrinks
    }
  }, [getLocationAtEvent, drawCanvasDots]);

  const handleContainerClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (propsRef.current.isSelectionMode) return;
    const found = getLocationAtEvent(e);
    if (found && propsRef.current.onLocationSelect) { e.stopPropagation(); propsRef.current.onLocationSelect(found); }
  }, [getLocationAtEvent]);

  const handleContainerMouseEnter = useCallback(() => { isHoveredRef.current = true; }, []);

  const handleContainerMouseLeave = useCallback(() => {
    isHoveredRef.current = false;
    hoveredPointIdRef.current = null;
    if (containerRef.current) containerRef.current.style.cursor = '';
    drawCanvasDots(propsRef.current.projectedPoints);
  }, [drawCanvasDots]);

  // FULL VIEW: frame everything the map is showing. The maths is the same fit-to-bounds the draw
  // effect already ran on first paint; it just had no way to be re-run, so once the user had zoomed
  // in there was no way back out short of reloading. Includes the asset pins when the layer is on,
  // which is the whole point: the store cloud and the assets it is judged against must be framed
  // together, or the comparison is off-screen.
  const handleFullView = useCallback(() => {
    const container = containerRef.current;
    if (!container || !zoomBehavior.current) return;
    const coords: Array<[number, number]> = [];
    for (const point of projectedPoints) coords.push([point.original.longitude!, point.original.latitude!]);
    if (showAssets) {
      for (const property of resolvedProperties as any[]) {
        const loc = property?.location as any;
        const lat = typeof loc?.lat === 'number' ? loc.lat : null;
        const lng = typeof loc?.lng === 'number' ? loc.lng : (typeof loc?.longitude === 'number' ? loc.longitude : null);
        if (lat != null && lng != null) coords.push([lng, lat]);
      }
    }
    if (coords.length === 0) return;
    const bounds = d3.geoPath().projection(projection.current).bounds({ type: 'MultiPoint', coordinates: coords } as any);
    const [[x0, y0], [x1, y1]] = bounds;
    const width = container.clientWidth;
    const height = container.clientHeight;
    // A single point has zero extent; without the guard the scale goes to Infinity.
    const spanX = Math.max(x1 - x0, 1e-6);
    const spanY = Math.max(y1 - y0, 1e-6);
    const scale = Math.max(1, Math.min(MAX_D3_SCALE, 0.85 / Math.max(spanX / width, spanY / height)));
    const translate: [number, number] = [
      width / 2 - scale * (x0 + x1) / 2,
      height / 2 - scale * (y0 + y1) / 2,
    ];
    d3.select(container)
      .transition()
      .duration(750)
      .ease(d3.easeCubicOut)
      .call(zoomBehavior.current.transform as any, d3.zoomIdentity.translate(translate[0], translate[1]).scale(scale));
  }, [projectedPoints, resolvedProperties, showAssets]);

  // Growing or shrinking the box invalidates the framing that was chosen for the old one: the same
  // transform that filled a 460px strip leaves the store cloud as a smudge in the middle of the
  // main area. Re-fit once the new size has actually landed - two frames, because the first is the
  // portal mount and the second is the layout it produces. Skipped on mount, where the draw effect
  // already frames, and where firing would fight it.
  //
  // Keyed on isExpanded ALONE. handleFullView is a useCallback over resolvedLocations, and the
  // `locations` prop arrives as a fresh `?? []` array on every parent render, so its identity
  // changes constantly - which meant this effect re-ran on any re-render and yanked the camera
  // back to the whole store cloud. Clicking an asset re-renders. That was the "it unzooms when I
  // click an asset" bug: not the framing in the draw effect, this. The callback is read through a
  // ref so the effect can depend on the expansion and nothing else.
  const expansionSettledRef = useRef(false);
  const handleFullViewRef = useRef(handleFullView);
  handleFullViewRef.current = handleFullView;

  // Expand where the host wired it, plain reframe otherwise. Read through the ref so the button's
  // handler does not change identity every time the store list does.
  const handleExpandOrFullView = useCallback(() => {
    if (onToggleExpand) onToggleExpand();
    else handleFullViewRef.current();
  }, [onToggleExpand]);
  useEffect(() => {
    if (!expansionSettledRef.current) { expansionSettledRef.current = true; return; }
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => { raf2 = requestAnimationFrame(() => handleFullViewRef.current()); });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, [isExpanded]);

  return (
    <div
        ref={containerRef}
        className="relative w-full h-full bg-transparent overflow-hidden group select-none"
        onContextMenu={handleContainerContextMenu}
        onClick={handleContainerClick}
        onMouseMove={handleContainerMouseMove}
        onMouseEnter={handleContainerMouseEnter}
        onMouseLeave={handleContainerMouseLeave}
    >
      {/* Bottom layer: the tiles. Everything below it in the tree paints on top, so the store
          points stay the most prominent thing on the map. */}
      {/* The inner div is the one mapbox-gl takes over: its own stylesheet sets
          `.mapboxgl-map { position: relative }`, which overrode `absolute inset-0` on the same
          element and collapsed the map to zero height, tiles loaded, nothing rendered. */}
      {BASEMAP_ACTIVE && (
        <div className="absolute inset-0 pointer-events-none">
          <div ref={basemapHostRef} className="h-full w-full [&_.mapboxgl-ctrl-logo]:!hidden" />
        </div>
      )}
      {/* The dot grid was the only texture when the map was empty; over real tiles it is noise. */}
      {!BASEMAP_ACTIVE && (
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={EMPTY_GRID_STYLE} />
      )}
      <svg ref={svgRef} width="100%" height="100%" className="absolute inset-0" shapeRendering="geometricPrecision"><g ref={mapGroupRef} /><g ref={scanLayerRef} /><g ref={targetLayerRef} /></svg>
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" />
      <div ref={tooltipRef} className="absolute pointer-events-none bg-[#353535] border border-white/[0.06] rounded-[12px] px-3 py-2 text-[#ECECEC] shadow-xl z-50 transition-opacity duration-150 opacity-0" style={TOOLTIP_ANCHOR_STYLE} />
      {onCountToggle ? (
        <button type="button" onClick={onCountToggle} aria-expanded={countOpen} className="absolute left-3 top-3 z-10 flex h-7 items-center gap-1 rounded-full border border-[#3A3A3A] bg-[#2A2A2A] pl-2.5 pr-2 text-[#ECECEC] transition-colors hover:bg-[#333333]" style={{ fontSize: 12 }}>
          {projectedPoints.length.toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-US')} {t.locsLabel}
          <ChevronDown size={13} className={`text-[#AFAFAF] transition-transform ${countOpen ? 'rotate-180' : ''}`} />
        </button>
      ) : (
        <div className="pointer-events-none absolute left-3 top-3 flex h-7 items-center rounded-full border border-[#3A3A3A] bg-[#2A2A2A] px-2.5 text-[#ECECEC]" style={{ fontSize: 12 }}>{projectedPoints.length.toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-US')} {t.locsLabel}</div>
      )}
      {/* MAP CONTROLS, bottom right. Deliberately the corner furthest from the store card, which
          pins to the top centre, and from the legend at top left. `pointer-events-auto` on the
          buttons only: the wrapper must not eat map drags. */}
      {showMapControls ? (
        <div className="absolute bottom-3 right-3 z-20 flex items-center gap-1.5">
          {onToggleAssets ? (
            <button
              type="button"
              onClick={onToggleAssets}
              title={showAssets ? t.assetsHide : t.assetsShow}
              aria-pressed={showAssets}
              // A control is coloured after the thing it controls. The active state was accent
              // green when the marker was a green pin; the marker is now the grey Landmark the
              // rest of the app uses for a Property, so "on" is a lit neutral, not a second hue.
              className={
                // ChatGPT's round pills (owner, 2026-09-28), solid so they read over the map.
                'pointer-events-auto inline-flex h-8 items-center gap-1.5 rounded-full border px-3 transition-colors ' +
                (showAssets
                  ? 'border-[#4A4A4A] bg-[#3A3A3A] text-white hover:bg-[#424242]'
                  : 'border-[#3A3A3A] bg-[#2A2A2A] text-[#E5E5E5] hover:bg-[#333333] hover:text-white')
              }
              style={{ fontSize: 13 }}
            >
              <Landmark size={14} strokeWidth={2} />
              {t.assetsLayer}
            </button>
          ) : null}
          {/* FULL VIEW IS ONE ACTION, NOT TWO. Framing everything inside a 460px box still leaves
              a 460px box, so where the map can take the main area it takes it AND frames, and the
              same button is how you come back. Where it cannot (the dev harness, the brand page),
              onToggleExpand is absent and this stays the pure reframe it always was. */}
          <button
            type="button"
            onClick={handleExpandOrFullView}
            title={isExpanded ? t.exitFullView : t.fullView}
            aria-label={isExpanded ? t.exitFullView : t.fullView}
            aria-pressed={onToggleExpand ? isExpanded : undefined}
            className="pointer-events-auto inline-flex h-8 items-center gap-1.5 rounded-full border border-[#3A3A3A] bg-[#2A2A2A] px-3 text-[#E5E5E5] transition-colors hover:bg-[#333333] hover:text-white"
            style={{ fontSize: 13 }}
          >
            {isExpanded ? <Minimize2 size={14} strokeWidth={2} /> : <Maximize2 size={14} strokeWidth={2} />}
            {isExpanded ? t.exitFullView : t.fullView}
          </button>
        </div>
      ) : null}
      <div className={`absolute top-4 right-4 pointer-events-none transition-opacity duration-300 ${isSelectionMode ? 'opacity-100' : 'opacity-0'}`}><div className="flex h-8 items-center gap-2 rounded-full border border-[#3A3A3A] bg-[#2A2A2A] px-3 text-[#ECECEC] backdrop-blur" style={{ fontSize: 13 }}><ScanLine size={14} /><span style={{ fontSize: 'inherit' }}>{t.selectTargetCountry}</span></div></div>
    </div>
  );
});

BrandLocationsMap.displayName = 'BrandLocationsMap';
export default BrandLocationsMap;
